<template>
  <div>
    <v-toolbar flat color="white">
      <v-toolbar-title>Uygulama Ayarları</v-toolbar-title>
      <v-divider class="mx-3" inset vertical></v-divider>
      <v-text-field 
        v-model="search" 
        append-icon="search" 
        label="Ara" 
        single-line 
        hide-details
      ></v-text-field>
      <v-spacer></v-spacer>
      <v-btn 
        color="primary" 
        dark 
        @click="showAddDialog" 
        class="mb-2"
      >
        <v-icon left>add</v-icon>
        Yeni Ayar Ekle
      </v-btn>
    </v-toolbar>

    <v-data-table
      :headers="headers"
      :loading="loading"
      :items="settings"
      :search="search"
      :rows-per-page-items="pageItems"
      :pagination.sync="pagination"
      class="elevation-1"
    >
      <template v-slot:items="props">
        <td class="text-xs-center">{{ props.item.id }}</td>
        <td class="text-xs-left"><strong>{{ props.item.key }}</strong></td>
        <td class="text-xs-left">{{ props.item.value }}</td>
        <td class="text-xs-left">{{ props.item.description || '-' }}</td>
        <td class="text-xs-center">
          <v-icon small class="mr-2" @click="editSetting(props.item)" color="blue">edit</v-icon>
          <v-icon small @click="deleteSetting(props.item)" color="red">delete</v-icon>
        </td>
      </template>
      
      <template v-slot:no-data>
        <div class="text-xs-center pa-4">
          <p>{{ loading ? 'Yükleniyor...' : 'Ayar bulunamadı.' }}</p>
        </div>
      </template>
    </v-data-table>

    <v-dialog v-model="dialog" max-width="600px" persistent>
      <v-card>
        <v-card-title>
          <span class="headline">{{ editedIndex === -1 ? 'Yeni Ayar' : 'Ayar Düzenle' }}</span>
        </v-card-title>
        <v-card-text>
          <v-container grid-list-md>
            <v-layout wrap>
              <v-flex xs12>
                <v-text-field 
                  v-model="editedItem.key" 
                  label="Anahtar (Key)" 
                  required
                  :disabled="editedIndex !== -1"
                  hint="Örn: FraudUpperLimit"
                  persistent-hint
                ></v-text-field>
              </v-flex>
              <v-flex xs12>
                <v-text-field 
                  v-model="editedItem.value" 
                  label="Değer (Value)" 
                  required
                  hint="Örn: 30000"
                  persistent-hint
                ></v-text-field>
              </v-flex>
              <v-flex xs12>
                <v-textarea 
                  v-model="editedItem.description" 
                  label="Açıklama"
                  rows="3"
                  hint="Bu ayarın ne için kullanıldığını açıklayın"
                  persistent-hint
                ></v-textarea>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" flat @click="close">İptal</v-btn>
          <v-btn 
            color="blue darken-1" 
            flat 
            @click="save" 
            :loading="saving"
            :disabled="!isFormValid"
          >
            Kaydet
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="500px">
      <v-card>
        <v-card-title class="headline">Ayar Sil</v-card-title>
        <v-card-text>
          <strong>{{ settingToDelete && settingToDelete.key }}</strong> ayarını silmek istediğinizden emin misiniz?
          <br><br>
          <v-alert type="warning" value="true">
            Bu işlem geri alınamaz!
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" flat @click="deleteDialog = false">İptal</v-btn>
          <v-btn 
            color="red darken-1" 
            flat 
            @click="confirmDelete"
            :loading="deleting"
          >
            Sil
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000">
      {{ snackbarText }}
      <v-btn dark flat @click="snackbar = false">Kapat</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  data() {
    return {
      search: '',
      loading: false,
      saving: false,
      deleting: false,
      dialog: false,
      deleteDialog: false,
      editedIndex: -1,
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      settingToDelete: null,
      pagination: {
        rowsPerPage: 25
      },
      pageItems: [10, 25, 50, 100],
      headers: [
        { text: 'ID', value: 'id', align: 'center', sortable: true },
        { text: 'Anahtar', value: 'key', align: 'left', sortable: true },
        { text: 'Değer', value: 'value', align: 'left', sortable: true },
        { text: 'Açıklama', value: 'description', align: 'left', sortable: true },
        { text: 'İşlemler', value: 'actions', align: 'center', sortable: false }
      ],
      editedItem: {
        key: '',
        value: '',
        description: ''
      },
      defaultItem: {
        key: '',
        value: '',
        description: ''
      }
    }
  },

  computed: {
    settings() {
      return this.$store.state.appSettings.all || [];
    },

    isFormValid() {
      return this.editedItem.key && this.editedItem.value;
    }
  },

  async mounted() {
    await this.initialize();
  },

  methods: {
    async initialize() {
      this.loading = true;
      try {
        await this.$store.dispatch('appSettings/getSettings');
      } catch (error) {
        this.showError('Ayarlar yüklenirken hata oluştu');
      } finally {
        this.loading = false;
      }
    },

    showAddDialog() {
      this.editedIndex = -1;
      this.editedItem = { ...this.defaultItem };
      this.dialog = true;
    },

    editSetting(item) {
      this.editedIndex = this.settings.indexOf(item);
      this.editedItem = { ...item };
      this.dialog = true;
    },

    deleteSetting(item) {
      this.settingToDelete = item;
      this.deleteDialog = true;
    },

    async confirmDelete() {
      this.deleting = true;
      try {
        await this.$store.dispatch('appSettings/deleteSetting', this.settingToDelete.id);
        this.showSuccess('Ayar başarıyla silindi');
        this.deleteDialog = false;
        this.settingToDelete = null;
      } catch (error) {
        const errorMessage = error.response && error.response.data 
          ? error.response.data 
          : 'Ayar silinirken hata oluştu';
        this.showError(errorMessage);
      } finally {
        this.deleting = false;
      }
    },

    async save() {
      if (!this.isFormValid) return;

      this.saving = true;
      try {
        if (this.editedIndex === -1) {
          await this.$store.dispatch('appSettings/postSetting', this.editedItem);
          this.showSuccess('Ayar başarıyla oluşturuldu');
        } else {
          await this.$store.dispatch('appSettings/putSetting', this.editedItem);
          this.showSuccess('Ayar başarıyla güncellendi');
        }
        this.close();
      } catch (error) {
        const errorMessage = error.response && error.response.data 
          ? error.response.data 
          : 'Ayar kaydedilirken hata oluştu';
        this.showError(errorMessage);
      } finally {
        this.saving = false;
      }
    },

    close() {
      this.dialog = false;
      this.editedItem = { ...this.defaultItem };
      this.editedIndex = -1;
    },

    showSuccess(message) {
      this.snackbarText = message;
      this.snackbarColor = 'success';
      this.snackbar = true;
    },

    showError(message) {
      this.snackbarText = message;
      this.snackbarColor = 'error';
      this.snackbar = true;
    }
  }
}
</script>

<style scoped>
</style>

